import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef, ReactNode } from 'react';
import { User, AuthState, LoginCredentials, RegisterData } from '../types';
import { authAPI, passkeysAPI, impersonationAPI } from '../services/api';
import {
  initTokenStore,
  getToken,
  setToken,
  clearToken,
  getAdminToken,
  setAdminToken,
  clearAdminToken,
} from '../services/tokenStore';
import { startAuthentication } from '@simplewebauthn/browser';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  loginWithToken: (token: string) => Promise<void>;
  loginWithPasskey: () => Promise<void>;
  startImpersonation: (userId: number) => Promise<void>;
  stopImpersonation: () => Promise<void>;
  isImpersonating: boolean;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: Partial<User>) => void;
}

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: { user: User; token: string } }
  | { type: 'UPDATE_USER'; payload: Partial<User> }
  | { type: 'LOGOUT' };

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: state.user ? { ...state.user, ...action.payload } : null,
      };
    case 'LOGOUT':
      return {
        ...initialState,
        isLoading: false,
      };
    default:
      return state;
  }
};

// Limpiar cache del API en el service worker
const clearApiCache = () => {
  if (navigator.serviceWorker?.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_API_CACHE' });
  }
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      // En nativo el token vive en el Keychain: hay que hidratar el store antes de
      // leerlo. En web es instantáneo (localStorage).
      await initTokenStore();
      const token = getToken();
      const userStr = localStorage.getItem('user');

      if (token && userStr) {
        try {
          const user = JSON.parse(userStr);

          // Restaurar sesion con datos locales inmediatamente
          dispatch({ type: 'SET_USER', payload: { user, token } });

          // Validar token contra el servidor en background
          // Si falla, no hacer logout inmediato (puede ser problema de red temporal)
          try {
            const response = await authAPI.getProfile();
            if (response.success && response.data?.user) {
              // Actualizar datos del usuario con los del servidor
              const freshUser = response.data.user;
              localStorage.setItem('user', JSON.stringify(freshUser));
              dispatch({ type: 'SET_USER', payload: { user: freshUser, token } });
            }
          } catch {
            // Error de red o token expirado - no hacer nada, el refresh token se encargara
            // cuando haga la primera llamada al API
          }
        } catch {
          clearToken();
          localStorage.removeItem('user');
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    checkAuth();
  }, []);

  // IMPORTANTE: todas estas funciones van con useCallback e identidad estable.
  // Si se recrean en cada render y algun componente las mete en las dependencias
  // de un useEffect, se produce un bucle infinito de llamadas al API (dispatch ->
  // re-render -> nueva identidad -> el efecto se vuelve a disparar).
  const login = useCallback(async (credentials: LoginCredentials) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await authAPI.login(credentials);
      if (response.success && response.data) {
        // Limpiar cache del API antes de guardar nuevo token
        // Evita que el SW devuelva datos del usuario anterior
        clearApiCache();

        setToken(response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        dispatch({ type: 'SET_USER', payload: response.data });
      } else {
        throw new Error(response.message || 'Error al iniciar sesion');
      }
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  }, []);

  const loginWithToken = useCallback(async (token: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      clearApiCache();
      setToken(token);

      // Obtener datos del usuario con el token
      const response = await authAPI.getProfile();
      if (response.success && response.data?.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
        dispatch({ type: 'SET_USER', payload: { user: response.data.user, token } });
      } else {
        throw new Error('No se pudo obtener el perfil');
      }
    } catch (error) {
      clearToken();
      localStorage.removeItem('user');
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  }, []);

  // Login con passkey. No pide email: el navegador ofrece las passkeys guardadas
  // para el dominio y el servidor deduce el usuario del credential_id firmado.
  const loginWithPasskey = useCallback(async () => {
    const optionsResponse = await passkeysAPI.authenticateBegin();
    if (!optionsResponse.success || !optionsResponse.data) {
      throw new Error(optionsResponse.message || 'No se pudo iniciar el acceso con passkey');
    }

    // Aqui el navegador pide Face ID / Touch ID / huella. Si el usuario cancela,
    // startAuthentication lanza y se propaga tal cual para distinguirlo de un fallo.
    const credential = await startAuthentication({ optionsJSON: optionsResponse.data });

    const response = await passkeysAPI.authenticateComplete(credential);
    if (!response.success || !response.data) {
      throw new Error(response.message || 'No se pudo verificar la passkey');
    }

    clearApiCache();
    setToken(response.data.token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
    dispatch({ type: 'SET_USER', payload: response.data });
  }, []);

  // --- Impersonacion -------------------------------------------------------
  // El token de impersonacion lleva dentro el adminId, asi que el backend puede
  // devolver la sesion de admin al terminar. Aun asi se guarda el token original
  // en localStorage: si el usuario cierra la pestaña a media impersonacion,
  // recuperarla es inmediato y no hay que volver a iniciar sesion.
  const startImpersonation = useCallback(async (userId: number) => {
    const response = await impersonationAPI.start(userId);
    if (!response.success || !response.data) {
      throw new Error(response.message || 'No se pudo iniciar la suplantación');
    }

    const currentToken = getToken();
    if (currentToken) {
      setAdminToken(currentToken);
    }

    // Imprescindible vaciar la cache del API: si no, el service worker podria
    // servir datos del admin al usuario suplantado.
    clearApiCache();
    setToken(response.data.impersonationToken);

    const profile = await authAPI.getProfile();
    if (profile.success && profile.data?.user) {
      localStorage.setItem('user', JSON.stringify(profile.data.user));
      dispatch({
        type: 'SET_USER',
        payload: { user: profile.data.user, token: response.data.impersonationToken },
      });
    }
  }, []);

  const stopImpersonation = useCallback(async () => {
    try {
      const response = await impersonationAPI.stop();
      if (response.success && response.data) {
        clearApiCache();
        setToken(response.data.adminToken);
        clearAdminToken();

        const profile = await authAPI.getProfile();
        if (profile.success && profile.data?.user) {
          localStorage.setItem('user', JSON.stringify(profile.data.user));
          dispatch({
            type: 'SET_USER',
            payload: { user: profile.data.user, token: response.data.adminToken },
          });
        }
        return;
      }
      throw new Error(response.message || 'No se pudo restaurar la sesión');
    } catch (error) {
      // Plan B: si el endpoint falla, se recupera el token de admin guardado en
      // local para no dejar a nadie atrapado dentro de otra cuenta.
      const savedAdminToken = getAdminToken();
      if (savedAdminToken) {
        clearApiCache();
        setToken(savedAdminToken);
        clearAdminToken();
        window.location.href = '/admin';
        return;
      }
      throw error;
    }
  }, []);

  const register = useCallback(async (data: RegisterData) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await authAPI.register(data);
      if (response.success && response.data) {
        clearApiCache();

        setToken(response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        dispatch({ type: 'SET_USER', payload: response.data });
      } else {
        throw new Error(response.message || 'Error al registrarse');
      }
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } finally {
      // Limpiar todo: localStorage + cache del API
      clearToken();
      localStorage.removeItem('user');
      clearApiCache();
      dispatch({ type: 'LOGOUT' });
    }
  }, []);

  // Se lee el usuario desde una ref para que updateUser no dependa de state.user
  // y mantenga una identidad estable entre renders.
  const userRef = useRef(state.user);
  userRef.current = state.user;

  const updateUser = useCallback((userData: Partial<User>) => {
    dispatch({ type: 'UPDATE_USER', payload: userData });
    if (userRef.current) {
      localStorage.setItem('user', JSON.stringify({ ...userRef.current, ...userData }));
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      ...state,
      login,
      loginWithToken,
      loginWithPasskey,
      startImpersonation,
      stopImpersonation,
      isImpersonating: Boolean(state.user?.isImpersonating),
      register,
      logout,
      updateUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
